import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { pointAPI } from '../../services/point';
import PaginationComponent from '../../components/PaginationComponent';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APPEAL_STATUS_BADGE = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
};

const APPEAL_STATUS_LABEL = {
  PENDING: 'Chờ duyệt',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
};

const POINT_STATUS_BADGE = {
  ACTIVE: 'primary',
  CANCELLED: 'secondary',
  APPEALED: 'warning',
};

const POINT_STATUS_LABEL = {
  ACTIVE: 'Có hiệu lực',
  CANCELLED: 'Đã miễn',
  APPEALED: 'Đang giải trình',
};

const CATEGORY_LABEL = {
  TRAFFIC: '🚗 Kẹt xe',
  PERSONAL_EMERGENCY: '🚨 Khẩn cấp',
  WEATHER: '🌧 Thời tiết',
  PUBLIC_TRANSPORT: '🚌 PT công cộng',
  HEALTH: '🏥 Sức khỏe',
  WORK_RELATED: '💼 Việc liên quan',
  SYSTEM_ERROR: '⚙️ Lỗi hệ thống',
  OTHER: '📋 Khác',
};

// #224004: 2 loại khiếu nại "báo cáo ca trước" chỉ ADMIN được duyệt — trước đây rơi vào nhánh
// `?? appeal.appeal_category` nên hiện ra mã thô, khó đọc trên màn duyệt.
const CATEGORY_LABEL_EXTRA = {
  PREVIOUS_SHIFT_MATERIAL_REPORT: '📦 Ca trước kiểm kê NVL sai',
  PREVIOUS_SHIFT_GOODS_RECEIPT_SKIPPED: '🚚 Ca trước bỏ qua nhập kho',
};

const categoryLabel = (c) => CATEGORY_LABEL[c] ?? CATEGORY_LABEL_EXTRA[c] ?? c ?? '-';

const CURRENT_MONTH = format(new Date(), 'yyyy-MM');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtDate = (v) => {
  if (!v) return '-';
  try { return format(new Date(v), 'dd/MM/yyyy HH:mm'); } catch { return v; }
};

const parseList = (res) => {
  // Nest: { data: [], meta: { total, page, limit, total_pages } }
  if (res && Array.isArray(res.data) && res.meta && typeof res.meta === 'object') {
    const m = res.meta;
    return {
      items: res.data,
      total: Number(m.total) || res.data.length,
      page: Number(m.page) || 1,
      limit: Number(m.limit) || 20,
      total_pages: Math.max(1, Number(m.total_pages) || 1),
    };
  }
  const payload = res?.data ?? res;
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, page: 1, limit: 20, total_pages: 1 };
  }
  const items = Array.isArray(payload?.data) ? payload.data : [];
  return {
    items,
    total: payload?.total ?? items.length,
    page: payload?.page ?? 1,
    limit: payload?.limit ?? 20,
    total_pages: payload?.total_pages ?? 1,
  };
};

// ---------------------------------------------------------------------------
// Sub-component: Appeal Review Modal
// ---------------------------------------------------------------------------

/**
 * #224004 mục 4 + mục 5 — khối chi tiết "Báo cáo ca trước" trong màn duyệt khiếu nại của ADMIN.
 *
 * Mục 5: hiện SONG SONG "Ca trước đã kiểm: X" và "Số thật NV báo đếm được: Y" ngay trong phiếu
 *        duyệt — trước đây ADMIN phải tự tra `reported_shift_check_id` mới thấy số cũ nên rất dễ
 *        duyệt cảm tính. `previous_actual_quantity` là SNAPSHOT lúc tạo báo cáo; bản ghi tạo
 *        trước #224004 chỉ có `original_quantity` nên phải fallback.
 * Mục 4: cảnh báo đỏ khi NV bị báo đã nghỉ việc / nghỉ phép / chuyển chi nhánh — hệ thống KHÔNG
 *        tự phạt và cũng KHÔNG tự miễn những ca này, ADMIN phải tự quyết.
 *
 * Khiếu nại loại khác không có `previous_shift_report` → trả null, màn duyệt giữ nguyên như cũ.
 */
function PreviousShiftReportDetail({ report }) {
  if (!report) return null;
  const items = Array.isArray(report.items) ? report.items : [];
  const orders = Array.isArray(report.purchase_orders) ? report.purchase_orders : [];

  return (
    <Card className="mb-3 border-warning">
      <Card.Body className="py-2 px-3">
        <div className="fw-bold small mb-2">
          🧾 Báo cáo của {report.reporter_employee?.full_name ?? 'NV khác'}
        </div>

        {report.needs_admin_manual_review && (
          <div className="alert alert-danger py-2 px-3 small mb-2" role="alert">
            ⚠️ <strong>Cần ADMIN xử lý thủ công.</strong>{' '}
            {report.manual_review_reason ?? 'Nhân viên bị báo cáo không còn ở trạng thái bình thường.'}
            <div className="mt-1">
              Hệ thống đã <strong>không tự động phạt</strong> và cũng không tự động miễn ca này.
            </div>
          </div>
        )}

        {items.length > 0 && (
          <Table size="sm" className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th>NVL</th>
                <th className="text-end">Ca trước đã kiểm</th>
                <th className="text-end">Số thật NV báo đếm được</th>
                <th className="text-end">Lệch</th>
                <th>Ảnh</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i, idx) => {
                const previous = i.previous_actual_quantity ?? i.original_quantity;
                const unit = i.material_unit ?? '';
                return (
                  <tr key={i.material_id ?? idx}>
                    <td>
                      {i.material_name ?? i.material_id}
                      {i.note ? <div className="text-muted small">{i.note}</div> : null}
                    </td>
                    <td className="text-end">{previous ?? '-'}{unit}</td>
                    <td className="text-end fw-bold">{i.reported_quantity ?? '-'}{unit}</td>
                    <td className="text-end">
                      {i.variance_pct === undefined || i.variance_pct === null ? (
                        <span className="text-muted">-</span>
                      ) : (
                        <Badge bg={i.beyond_threshold ? 'danger' : 'secondary'}>
                          {i.variance_pct}%
                        </Badge>
                      )}
                    </td>
                    <td>
                      {i.photo_url ? (
                        <a href={i.photo_url} target="_blank" rel="noreferrer">Xem</a>
                      ) : <span className="text-muted small">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}

        {orders.length > 0 && (
          <div className="small">
            Đơn/phiếu bị bỏ qua nhập kho:{' '}
            {orders.map((o) => o.order_number).filter(Boolean).join(', ')}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function AppealReviewModal({ show, appeal, onClose, onSuccess, managerId, isAdmin }) {
  const [context, setContext] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [mode, setMode] = useState('exempt'); // 'exempt' | 'reduce' | 'reject'
  const [reduceTo, setReduceTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // #005638 — ô chọn "Người chịu trách nhiệm" (chỉ ADMIN, chỉ loại PREVIOUS_SHIFT_MATERIAL_REPORT)
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [responsibleId, setResponsibleId] = useState('');

  // Ô chọn chỉ có nghĩa cho đúng loại khiếu nại "Báo cáo ca trước kiểm kê NVL sai" và chỉ ADMIN
  // được thao tác (BE cũng chặn lại y hệt — FE chỉ để đỡ bấm nhầm, không phải lớp bảo mật).
  // Nhận diện theo `source_type` chứ KHÔNG theo `appeal_category`: đường khiếu nại từ app Ồn Staff
  // (`/history`) không gửi category lên nên trường đó có thể null, còn `source_type` luôn đúng.
  const canPickResponsible =
    !!isAdmin
    && appeal?.source_type === 'PREVIOUS_SHIFT_MATERIAL_REPORT'
    && !!appeal?.previous_shift_report;

  useEffect(() => {
    if (!show || !appeal?.id) return;
    setMode('exempt');
    setReduceTo('');
    setContext(null);
    setLoadingCtx(true);
    pointAPI.getAppealContext(appeal.id)
      .then((res) => {
        const body = res?.data ?? res;
        setContext(body?.context ?? null);
      })
      .catch(() => setContext(null))
      .finally(() => setLoadingCtx(false));
  }, [show, appeal?.id]);

  useEffect(() => {
    // Mặc định LUÔN là người đang bị phạt (`created_by` gốc) — không đổi gì nếu ADMIN không động vào.
    setResponsibleId(appeal?.employee_id ?? '');
    setCandidates([]);
    if (!show || !appeal?.id || !canPickResponsible) return;
    setLoadingCandidates(true);
    pointAPI.getResponsibleCandidates(appeal.id)
      .then((res) => {
        const body = res?.data ?? res;
        setCandidates(Array.isArray(body?.candidates) ? body.candidates : []);
      })
      .catch(() => setCandidates([]))
      .finally(() => setLoadingCandidates(false));
  }, [show, appeal?.id, appeal?.employee_id, canPickResponsible]);

  // Người ADMIN đã chọn KHÁC người đang bị phạt (null nếu giữ mặc định).
  const reassignedTo =
    canPickResponsible && responsibleId && responsibleId !== appeal?.employee_id
      ? candidates.find((c) => c.employee_id === responsibleId) ?? null
      : null;

  const handleSubmit = async () => {
    if (!appeal?.id) return;
    try {
      setSubmitting(true);
      if (mode === 'reject') {
        await pointAPI.reviewAppeal(appeal.id, managerId, false);
        toast.success('Đã từ chối giải trình');
      } else if (mode === 'reduce') {
        const val = parseInt(reduceTo, 10);
        if (isNaN(val) || val < 0) { toast.error('Nhập số điểm hợp lệ'); return; }
        await pointAPI.reviewAppeal(appeal.id, managerId, true, val);
        toast.success(`Đã giảm phạt xuống còn ${val} điểm`);
      } else {
        // #005638: chỉ gửi `responsible_employee_id` khi ADMIN đổi sang người KHÁC người đang bị
        // phạt — giữ nguyên payload cũ trong mọi trường hợp còn lại.
        const reassignTo = reassignedTo?.employee_id;
        await pointAPI.reviewAppeal(appeal.id, managerId, true, undefined, reassignTo);
        toast.success(
          reassignTo
            ? `Đã gỡ phạt và chuyển trách nhiệm sang ${reassignedTo.full_name}`
            : 'Đã miễn phạt toàn bộ',
        );
      }
      onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Thao tác thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  if (!appeal) return null;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Duyệt giải trình</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>{appeal.employee?.full_name ?? appeal.employee_id}</strong>
          <div className="text-muted small">Điểm phạt: <span className="text-danger">-{appeal.value} điểm</span></div>
          {appeal.appeal_category && (
            <div className="text-muted small">Loại: {categoryLabel(appeal.appeal_category)}</div>
          )}
          <div className="mt-1">{appeal.appeal_reason}</div>
        </div>

        <PreviousShiftReportDetail report={appeal.previous_shift_report} />

        {loadingCtx ? (
          <div className="text-center py-2"><Spinner size="sm" /></div>
        ) : context ? (
          <Card className="mb-3 bg-light border-0">
            <Card.Body className="py-2 px-3">
              <Row className="text-center">
                <Col><div className="fw-bold">{context.late_count_this_month ?? 0}</div><small className="text-muted">Phạt ca (tháng)</small></Col>
                <Col><div className="fw-bold">{context.appeal_count_this_month ?? 0}</div><small className="text-muted">Giải trình (tháng)</small></Col>
                <Col><div className="fw-bold text-success">{context.approved_appeal_count ?? 0}</div><small className="text-muted">Được duyệt</small></Col>
                <Col><div className="fw-bold text-danger">{context.total_penalty_this_month ?? 0}</div><small className="text-muted">Tổng phạt</small></Col>
              </Row>
            </Card.Body>
          </Card>
        ) : null}

        <Form>
          <Form.Check
            type="radio" id="mode-exempt" label="✅ Miễn phạt toàn bộ"
            checked={mode === 'exempt'} onChange={() => setMode('exempt')} className="mb-2"
          />
          {/* #005638 (US-201742 mục 6, Phương án B): ADMIN sửa TAY người chịu trách nhiệm ngay
              tại bước duyệt. Mặc định vẫn là người hệ thống tự gán (created_by lần kiểm CLOSING
              ca trước) — không đổi gì nếu ADMIN không động vào. Chỉ có nghĩa ở chế độ "Miễn phạt
              toàn bộ": từ chối khiếu nại = giữ nguyên phạt cho chính người đó. */}
          {canPickResponsible && mode === 'exempt' && (
            <Form.Group className="mb-3 ms-4">
              <Form.Label className="small fw-bold mb-1">Người chịu trách nhiệm</Form.Label>
              {loadingCandidates ? (
                <div className="text-muted small"><Spinner size="sm" /> Đang tải nhân viên trong ca...</div>
              ) : (
                <>
                  <Form.Select
                    size="sm"
                    value={responsibleId}
                    onChange={(e) => setResponsibleId(e.target.value)}
                    style={{ maxWidth: 380 }}
                  >
                    {candidates.map((c) => (
                      <option key={c.employee_id} value={c.employee_id}>
                        {c.full_name}
                        {c.is_current ? ' (mặc định — hệ thống đã gán)' : ''}
                        {c.check_in_time
                          ? ` — vào ca ${format(new Date(c.check_in_time), 'HH:mm dd/MM')}`
                          : ''}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Danh sách lấy từ chấm công của ca bị báo cáo. Giữ nguyên mặc định nếu không cần đổi.
                  </Form.Text>
                  {reassignedTo && (
                    <div className="alert alert-warning py-2 px-3 small mt-2 mb-0" role="alert">
                      Điểm phạt sẽ <strong>chuyển hẳn</strong> sang{' '}
                      <strong>{reassignedTo.full_name}</strong>;{' '}
                      {appeal?.employee?.full_name ?? 'người đang bị phạt'} được gỡ phạt.
                      Báo cáo vẫn tính là <strong>đúng</strong> nên người báo cáo vẫn được thưởng.
                    </div>
                  )}
                </>
              )}
            </Form.Group>
          )}
          <Form.Check
            type="radio" id="mode-reduce" label="📉 Giảm phạt (nhập số điểm còn lại)"
            checked={mode === 'reduce'} onChange={() => setMode('reduce')} className="mb-2"
          />
          {mode === 'reduce' && (
            <Form.Group className="mb-2 ms-4">
              <Form.Control
                type="number" min={0} max={appeal.value} placeholder="Số điểm còn lại (vd: 1)"
                value={reduceTo} onChange={(e) => setReduceTo(e.target.value)}
                style={{ width: 180 }}
              />
              <Form.Text className="text-muted">Điểm gốc: {appeal.value}</Form.Text>
            </Form.Group>
          )}
          <Form.Check
            type="radio" id="mode-reject" label="❌ Từ chối — Giữ nguyên phạt"
            checked={mode === 'reject'} onChange={() => setMode('reject')}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Hủy</Button>
        <Button
          variant={mode === 'reject' ? 'danger' : 'success'}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? <Spinner size="sm" /> : 'Xác nhận'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Manual Point Modal
// ---------------------------------------------------------------------------

function ManualPointModal({ show, onClose, onSuccess, managerId }) {
  const [form, setForm] = useState({ employee_id: '', type: 'ADD', value: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.employee_id.trim()) { toast.error('Nhập ID nhân viên'); return; }
    const val = parseInt(form.value, 10);
    if (isNaN(val) || val <= 0) { toast.error('Nhập số điểm hợp lệ (> 0)'); return; }
    if (!form.reason.trim()) { toast.error('Nhập lý do'); return; }
    try {
      setSubmitting(true);
      await pointAPI.createManualPoint({
        employee_id: form.employee_id.trim(),
        type: form.type,
        value: val,
        reason: form.reason.trim(),
        current_user_id: managerId,
      });
      toast.success('Đã tạo điểm thủ công');
      setForm({ employee_id: '', type: 'ADD', value: '', reason: '' });
      onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Tạo điểm thất bại');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Thêm điểm thủ công</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>ID nhân viên</Form.Label>
            <Form.Control
              placeholder="UUID nhân viên"
              value={form.employee_id}
              onChange={(e) => update('employee_id', e.target.value)}
            />
          </Form.Group>
          <Row className="mb-3">
            <Col>
              <Form.Label>Loại</Form.Label>
              <Form.Select value={form.type} onChange={(e) => update('type', e.target.value)}>
                <option value="ADD">➕ Cộng điểm</option>
                <option value="SUBTRACT">➖ Trừ điểm</option>
              </Form.Select>
            </Col>
            <Col>
              <Form.Label>Số điểm</Form.Label>
              <Form.Control
                type="number" min={1} placeholder="Vd: 5"
                value={form.value} onChange={(e) => update('value', e.target.value)}
              />
            </Col>
          </Row>
          <Form.Group>
            <Form.Label>Lý do</Form.Label>
            <Form.Control
              as="textarea" rows={2} placeholder="Ghi chú lý do..."
              value={form.reason} onChange={(e) => update('reason', e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>Hủy</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? <Spinner size="sm" /> : 'Tạo điểm'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Pending Appeals
// ---------------------------------------------------------------------------

function PendingAppealsTab({ branchId, managerId, isAdmin, onCountChange }) {
  const [appeals, setAppeals] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);

  const fetchAppeals = useCallback(async (page = meta.page) => {
    try {
      setLoading(true);
      const res = await pointAPI.getPendingAppeals({ branch_id: branchId, page, limit: 20 });
      const { items, total, total_pages } = parseList(res);
      setAppeals(items);
      setMeta((prev) => ({ ...prev, total, page, total_pages }));
      onCountChange?.(total);
    } catch (err) {
      toast.error(err?.message || 'Không tải được danh sách giải trình');
      setAppeals([]);
    } finally {
      setLoading(false);
    }
  }, [branchId, onCountChange]);

  useEffect(() => {
    fetchAppeals(1);
  }, [branchId, fetchAppeals]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">Tổng: {meta.total} giải trình chờ duyệt</span>
        <Button size="sm" variant="outline-secondary" onClick={() => fetchAppeals(meta.page)}>
          Làm mới
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-4"><Spinner /></div>
      ) : appeals.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có giải trình chờ duyệt</div>
      ) : (
        <Table responsive hover size="sm">
          <thead className="table-light">
            <tr>
              <th>Nhân viên</th>
              <th>Điểm phạt</th>
              <th>Loại lý do</th>
              <th>Lý do giải trình</th>
              <th>Gửi lúc</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {appeals.map((a) => (
              <tr key={a.id}>
                <td>
                  <div className="fw-medium">{a.employee?.full_name ?? '-'}</div>
                  <div className="text-muted small">{a.employee?.branch?.name ?? ''}</div>
                  {/* #224004 mục 4: thấy ngay từ danh sách, không phải mở từng phiếu mới biết */}
                  {a.previous_shift_report?.needs_admin_manual_review && (
                    <Badge bg="danger" className="mt-1">⚠️ Cần xử lý thủ công</Badge>
                  )}
                </td>
                <td><span className="text-danger fw-bold">-{a.value}</span></td>
                <td><span className="text-muted small">{categoryLabel(a.appeal_category)}</span></td>
                <td style={{ maxWidth: 240 }}>
                  <div className="text-truncate" title={a.appeal_reason}>{a.appeal_reason}</div>
                </td>
                <td className="text-nowrap text-muted small">{fmtDate(a.appeal_submitted_at ?? a.updated_at)}</td>
                <td>
                  <Button size="sm" variant="outline-primary" onClick={() => setReviewTarget(a)}>
                    Duyệt
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {meta.total_pages > 1 && (
        <PaginationComponent
          currentPage={meta.page}
          totalPages={meta.total_pages}
          onPageChange={(p) => fetchAppeals(p)}
        />
      )}

      <AppealReviewModal
        show={!!reviewTarget}
        appeal={reviewTarget}
        managerId={managerId}
        isAdmin={isAdmin}
        onClose={() => setReviewTarget(null)}
        onSuccess={() => { setReviewTarget(null); fetchAppeals(meta.page); }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Point History
// ---------------------------------------------------------------------------

/**
 * #191439 — Tab này là ĐÍCH của link sâu trong 6 tin về điểm/giải trình (dòng 48, 50, 52, 61,
 * 63, 65 của bảng khảo sát #132320). Tin kèm `?employee_id=` để mở sẵn đúng nhân viên.
 *
 * Vì sao `employee_id` chứ không phải `q` như bảng khảo sát đề nghị: API `pointAPI.getPoints`
 * KHÔNG có tham số tìm tự do theo tên — nó lọc theo `employee_id`. Gửi `q=<tên NV>` thì trang
 * mở ra danh sách KHÔNG lọc gì cả, tức link sâu chỉ đúng trên giấy. `employee_id` snake_case
 * là NGOẠI LỆ ĐÃ ĐƯỢC CHỐT cho Ồn Coffee ở `KHUON-CHUAN-URL-PARAM.md` mục 1bis, không phải
 * tên tự đặt.
 */
const POINT_HISTORY_FILTER_SPEC = {
  employee_id: { default: '' },
  month: { default: CURRENT_MONTH, valid: (v) => /^\d{4}-\d{2}$/.test(v) },
  type: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => ['ADD', 'SUBTRACT'].includes(v.toUpperCase()),
  },
  appeal_status: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => ['PENDING', 'APPROVED', 'REJECTED'].includes(v.toUpperCase()),
  },
};

function PointHistoryTab({ branchId }) {
  const [points, setPoints] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 15, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters, , { ignored, dismissIgnored }] = useFilterParams(
    POINT_HISTORY_FILTER_SPEC,
  );
  const updateFilter = (field, value) => setFilters({ [field]: value });

  const fetchPoints = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        employee_id: filters.employee_id.trim() || undefined,
        month: filters.month || undefined,
        type: filters.type || undefined,
        appeal_status: filters.appeal_status || undefined,
        branch_id: branchId || undefined,
        page,
        limit: meta.limit,
      };
      const res = await pointAPI.getPoints(params);
      const { items, total, total_pages } = parseList(res);
      setPoints(items);
      setMeta((prev) => ({ ...prev, total, page, total_pages }));
    } catch (err) {
      toast.error(err?.message || 'Không tải được lịch sử điểm');
      setPoints([]);
    } finally {
      setLoading(false);
    }
  }, [filters, branchId, meta.limit]);

  useEffect(() => {
    fetchPoints(1);
  }, [filters, branchId, fetchPoints]);

  return (
    <>
      <InvalidParamsNotice
        ignored={ignored}
        onDismiss={dismissIgnored}
      />
      <Row className="mb-3 g-2 align-items-end">
        <Col xs={12} sm={4} md={3}>
          <Form.Control
            size="sm" placeholder="ID nhân viên"
            value={filters.employee_id}
            onChange={(e) => updateFilter('employee_id', e.target.value)}
          />
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Control
            size="sm" type="month"
            value={filters.month}
            onChange={(e) => updateFilter('month', e.target.value)}
          />
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={filters.type} onChange={(e) => updateFilter('type', e.target.value)}>
            <option value="">Tất cả loại</option>
            <option value="ADD">Cộng điểm</option>
            <option value="SUBTRACT">Trừ điểm</option>
          </Form.Select>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={filters.appeal_status} onChange={(e) => updateFilter('appeal_status', e.target.value)}>
            <option value="">Tất cả KN</option>
            <option value="PENDING">Chờ duyệt</option>
            <option value="APPROVED">Đã duyệt</option>
            <option value="REJECTED">Từ chối</option>
          </Form.Select>
        </Col>
      </Row>

      <div className="text-muted small mb-2">Tổng: {meta.total} bản ghi</div>

      {loading ? (
        <div className="text-center py-4"><Spinner /></div>
      ) : points.length === 0 ? (
        <div className="text-center py-4 text-muted">Không có dữ liệu</div>
      ) : (
        <Table responsive hover size="sm">
          <thead className="table-light">
            <tr>
              <th>Nhân viên</th>
              <th>Loại</th>
              <th>Điểm</th>
              <th>Lý do</th>
              <th>Trạng thái</th>
              <th>Khiếu nại</th>
              <th>Lý do giải trình</th>
              <th>Thời gian</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.id}>
                <td>
                  <div className="fw-medium">{p.employee?.full_name ?? p.employee_id?.slice(0, 8)}</div>
                </td>
                <td>
                  <Badge bg={p.type === 'ADD' ? 'success' : 'danger'}>
                    {p.type === 'ADD' ? 'Cộng' : 'Trừ'}
                  </Badge>
                </td>
                <td className="fw-bold">{p.type === 'ADD' ? '+' : '-'}{p.value}</td>
                <td style={{ maxWidth: 200 }}>
                  <div className="text-truncate small" title={p.reason}>{p.reason ?? '-'}</div>
                </td>
                <td>
                  <Badge bg={POINT_STATUS_BADGE[p.status] ?? 'secondary'}>
                    {POINT_STATUS_LABEL[p.status] ?? p.status}
                  </Badge>
                </td>
                <td>
                  {p.appeal_status ? (
                    <Badge bg={APPEAL_STATUS_BADGE[p.appeal_status] ?? 'secondary'}>
                      {APPEAL_STATUS_LABEL[p.appeal_status] ?? p.appeal_status}
                    </Badge>
                  ) : (
                    '-'
                  )}
                </td>
                <td style={{ maxWidth: 180 }}>
                  {p.appeal_reason ? (
                    <div className="text-truncate small" title={p.appeal_reason}>
                      {p.appeal_reason}
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
                <td className="text-nowrap text-muted small">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {meta.total_pages > 1 && (
        <PaginationComponent
          currentPage={meta.page}
          totalPages={meta.total_pages}
          onPageChange={(p) => fetchPoints(p)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function PointManagementPage() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const { search } = useLocation();
  const branchId = selectedBranch?.id;
  const managerId = user?.id;
  // #005638: chỉ ADMIN được sửa tay người chịu trách nhiệm (MANAGER vốn đã không duyệt được loại
  // khiếu nại này — BE chặn ở `reviewAppeal`).
  const isAdmin = user?.role === 'ADMIN';

  const [pendingCount, setPendingCount] = useState(0);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualRefresh, setManualRefresh] = useState(0);

  // Nếu URL có ?appeal_status=PENDING thì mở tab giải trình ngay
  // #191439: giữ nguyên lối cũ `?appeal_status=PENDING` (link đang chạy, không được gãy) và
  // thêm `?tab=` theo bộ tên chuẩn (`KHUON-CHUAN-URL-PARAM.md` mục 1) cho link sâu mới.
  const searchParams = new URLSearchParams(search);
  const requestedTab = searchParams.get('tab');
  const defaultTab = ['appeals', 'history'].includes(requestedTab ?? '')
    ? requestedTab
    : searchParams.get('appeal_status') === 'PENDING'
      ? 'appeals'
      : 'history';

  const [activeTab, setActiveTab] = useState(defaultTab);

  return (
    <PageLayout>
      <Row className="mb-3 align-items-center">
        <Col>
          <h5 className="mb-0">Quản lý điểm nhân viên</h5>
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="primary" onClick={() => setShowManualModal(true)}>
            + Thêm điểm thủ công
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
            <Tab
              eventKey="appeals"
              title={
                <>
                  Giải trình chờ duyệt{' '}
                  {pendingCount > 0 && (
                    <Badge bg="danger" pill className="ms-1">{pendingCount}</Badge>
                  )}
                </>
              }
            >
              <PendingAppealsTab
                key={`appeals-${branchId}`}
                branchId={branchId}
                managerId={managerId}
                isAdmin={isAdmin}
                onCountChange={setPendingCount}
              />
            </Tab>
            <Tab eventKey="history" title="Lịch sử điểm">
              <PointHistoryTab
                key={`history-${branchId}-${manualRefresh}`}
                branchId={branchId}
              />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <ManualPointModal
        show={showManualModal}
        managerId={managerId}
        onClose={() => setShowManualModal(false)}
        onSuccess={() => {
          setShowManualModal(false);
          setManualRefresh((n) => n + 1);
        }}
      />
    </PageLayout>
  );
}
